import React, { memo, useEffect, useRef, useState } from "react";
import styles from "./Sidebar.module.scss";
import { sideBarNavigation } from "@/lib/sidebarNavigation";
import { NavLink, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  combineClasses,
  getItem,
  hasAnyPermission,
} from "@/lib/utils";
import { toggleSidebar, setSidebarOpen } from "@/store/slices/globalSlice";
import ArrowIcon from "@/components/SVGComponents/ArrowIcon";
import type { RootState } from "@/store/rootReducer";
import type { SidebarItem } from "@/lib/sidebarNavigation";
import { FiberManualRecord } from "@mui/icons-material";
import { Button } from "@mui/material";
import AttendanceReport from "@/pages/Attendance/Components/AttendanceReport";
import LocationReport from "@/pages/Reports/LocationReport";
import { usePermissions } from "@/hooks/usePermissions";

const Sidebar: React.FC = () => {
  const { permissions, permissionLoading } = usePermissions();
  const sidebarOpen = useSelector((state: RootState) => state.global.sidebarOpen);
  const [openDropdowns, setOpenDropdowns] = useState<string[]>([]);
  const dispatch = useDispatch();
  const layoutConf = getItem("layoutConfig");
  const location = useLocation();
  const openedByHover = useRef(false);
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const [locationReportOpen, setLocationReportOpen] = useState(false);

  const isReady = !permissionLoading;

  const isRouteActive = (path: string) => {
    const current = location.pathname.replace(/\/+$/, "");
    const target = path.replace(/\/+$/, "");
    return current === target || current.startsWith(`${target}/`);
  };

  const findActiveParents = (
    items: SidebarItem[],
    path: string,
    parents: string[] = []
  ): string[] => {
    for (let item of items) {
      const newParents = [...parents, item.id];

      if (item.children && item.children.length > 0) {
        const childMatch = findActiveParents(item.children, path, newParents);
        if (childMatch.length) return childMatch;
      }

      if (!item.children?.length && isRouteActive(item.path)) {
        return newParents;
      }
    }
    return [];
  };

  useEffect(() => {
    if (!sidebarOpen) {
      setOpenDropdowns([]);
      return;
    }
    const activeParents = findActiveParents(
      sideBarNavigation,
      location.pathname
    );
    setOpenDropdowns(activeParents);
  }, [location.pathname, sidebarOpen]);

  const toggleDropdown = (id: string, level = 0) => () => {
    setOpenDropdowns((prev) => {
      if (prev[level] === id) {
        return prev.slice(0, level);
      }
      return [...prev.slice(0, level), id];
    });
  };

  const renderNavItems = (items: SidebarItem[], level = 0) => {
    return items
      .filter((item) => {
        const hasParentPermission =
          !item.permission || item.permission.length === 0
            ? true
            : hasAnyPermission(permissions ?? [], item.permission);

        if (item.children && item.children.length > 0) {
          const filteredChildren = item.children.filter(
            (child) =>
              !child.permission || child.permission.length === 0 ||
              hasAnyPermission(permissions ?? [], child.permission)
          );

          return (
            hasParentPermission &&
            filteredChildren.length > 0
          );
        }

        return hasParentPermission;
      })
      .map((item) => {
        const hasChildren = !!item.children?.length;
        const paddingLeft = 16 + level * 18;

        return (
          <div key={item.id}>
            <NavLink
              to={item.path}
              end={!hasChildren}
              className={combineClasses(
                styles.navLink,
                !sidebarOpen && styles.sidebarClosed,
                openDropdowns.includes(item.id) && styles.activeLink
              )}
              style={{ paddingLeft }}
              onClick={(e) => {
                if (item.pageName === "Attendance Report") {
                  e.preventDefault();
                  setAttendanceOpen(true);
                  return;
                }
                if (item.pageName === "Location Report") {
                  e.preventDefault();
                  setLocationReportOpen(true);
                  return;
                }
                if (hasChildren) {
                  e.preventDefault();
                  toggleDropdown(item.id, level)();
                }
              }}
            >
              {/* Nested dot */}
              {level > 0 && <FiberManualRecord sx={{ fontSize: 6 }} />}

              {/* Icon only for top level */}
              {item.icon && level === 0 && (
                <img
                  src={String(item.icon)}
                  width={20}
                  style={{ marginRight: 6 }}
                />
              )}

              <span>{item.pageName}</span>

              {hasChildren && (
                <ArrowIcon
                  className={styles.arrowIcon}
                  style={{
                    width: "auto",
                    height: "7px",
                    flexShrink: 0,
                    marginLeft: "auto",
                    transform: openDropdowns.includes(item.id)
                      ? "rotate(90deg)"
                      : "rotate(0deg)",
                    transition: "transform ease 300ms",
                  }}
                />
              )}
            </NavLink>

            {hasChildren && openDropdowns.includes(item.id) && (
              <div>{renderNavItems(item.children || [], level + 1)}</div>
            )}
          </div>
        );
      });
  };

  return (
    <>
      <div
        className={combineClasses(
          styles.leftSideContainer,
          !sidebarOpen ? styles.sidebarClosed : styles.sidebarOpen
        )}
      >
        {/* Logo */}
        <div className={styles.logoContainer}>
          <div className={styles.iconContainer}>
            <div
              className={styles.mainIconDiv}
              style={{
                justifyContent: sidebarOpen ? "space-around" : "center",
              }}
            >
              <div className="flex items-center gap-3" >
                <img
                  src="/FFC-logo.png"
                  alt="GD logo"
                  className={styles.mainIconImage}
                />
                <div className={styles.webAppTitle}>
                  {layoutConf?.WebAppTitle}
                </div>
              </div>

              <div>
                <Button onClick={() => dispatch(toggleSidebar())} sx={{
                  display: "flex",
                  justifyContent: sidebarOpen ? "flex-end" : "center",
                  alignItems: "center",
                  width: "100%",
                  minWidth: "auto",
                  padding: "4px"
                }}>
                  <img
                    src="/assets/svg/Angle-double-left.svg"
                    className={styles.toggleIcon}
                    style={{
                      transform: sidebarOpen
                        ? "rotate(0deg)"
                        : "rotate(180deg)",
                    }}
                  />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div
          className={styles.navContainer}
          onMouseOver={() => {
            if (!sidebarOpen) {
              dispatch(setSidebarOpen(true));
              openedByHover.current = true;
            }
          }}
          onMouseLeave={() => {
            if (openedByHover.current) {
              dispatch(setSidebarOpen(false));
              openedByHover.current = false;
            }
          }}
        >
          {isReady && renderNavItems(sideBarNavigation)}
        </div>
      </div>
      {attendanceOpen && (
        <AttendanceReport
          openReportModal={attendanceOpen}
          setOpenReportModal={() => setAttendanceOpen(false)} />
      )}

      {locationReportOpen && (
        <LocationReport
          openReportModal={locationReportOpen}
          setOpenReportModal={() => setLocationReportOpen(false)} />
      )}
    </>

  );
};

export default memo(Sidebar);